import { CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import { RgbaColor, RgbaColorPicker, HexColorInput } from 'react-colorful';
import chroma from 'chroma-js';
import { debounce } from 'underscore';

import { ColorPickerProps, OnColorPickerChangeResult } from './color-picker.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import RGBAInputs from './rgba-inputs';
import EyeDropper from './eye-dropper/eye-dropper';
import PresetColors from './preset-colors';

import './color-picker.css';

const ColorPicker = memo(
  ({
    initialColor,
    value,
    onChange,
    containerClassName,
    pickerClassName,
    style,
    presetColors,
    presetColorsLabel,
    saturationPickerHeight,
    width,
    boxShadow,
  }: ColorPickerProps) => {
    const [color, setColor] = useState<{ hex: string; rgba: RgbaColor }>();
    const [red, setRed] = useState('100');
    const [green, setGreen] = useState('100');
    const [blue, setBlue] = useState('100');
    const [alpha, setAlpha] = useState('1');

    useEffect(() => {
      if (color) return;

      const isValidColor = chroma.valid(initialColor);

      if (!initialColor || !isValidColor) {
        const startingColor = { r: 50, g: 100, b: 150, a: 1 };
        setColor({
          rgba: startingColor,
          hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
        });
        setRed(startingColor.r.toString());
        setGreen(startingColor.g.toString());
        setBlue(startingColor.b.toString());
        setAlpha(startingColor.a.toString());
        return;
      }

      const hex = chroma(initialColor).hex();
      const rgba = chroma(initialColor).rgba();
      setColor({
        rgba: {
          r: rgba[0],
          g: rgba[1],
          b: rgba[2],
          a: rgba[3],
        },
        hex: hex,
      });
      setRed(rgba[0].toString());
      setGreen(rgba[1].toString());
      setBlue(rgba[2].toString());
      setAlpha(rgba[3].toString());
    }, [color, initialColor]);

    /* if value is used then it's a controlled component and we should update color based on that value */
    useEffect(() => {
      if (value === undefined) return;

      const isValidColor = chroma.valid(value);

      if (!value || !isValidColor) {
        const startingColor = { r: 50, g: 100, b: 150, a: 1 };
        setColor({
          rgba: startingColor,
          hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
        });
        setRed(startingColor.r.toString());
        setGreen(startingColor.g.toString());
        setBlue(startingColor.b.toString());
        setAlpha(startingColor.a.toString());
        return;
      }

      const hex = chroma(value).hex();
      const rgba = chroma(value).rgba();
      setColor({
        rgba: {
          r: rgba[0],
          g: rgba[1],
          b: rgba[2],
          a: rgba[3],
        },
        hex: hex,
      });
      setRed(rgba[0].toString());
      setGreen(rgba[1].toString());
      setBlue(rgba[2].toString());
      setAlpha(rgba[3].toString());
    }, [value]);

    const debouncedChange = useRef(
      debounce((color: OnColorPickerChangeResult) => {
        onChange?.(color);
      }, 100)
    ).current;

    const handleColorPickerChange = useCallback(
      (color: RgbaColor) => {
        const rgbaString = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
        const hex = chroma(rgbaString).hex();
        setColor({
          rgba: color,
          hex,
        });
        setRed(color.r.toString());
        setGreen(color.g.toString());
        setBlue(color.b.toString());
        setAlpha(color.a.toString());
        debouncedChange?.({
          rgba: rgbaString,
          hex,
          rgb: `rgb(${color.r}, ${color.g}, ${color.b})`,
          opacity: color.a,
        });
      },
      [debouncedChange]
    );

    const handleHexInputChange = useCallback(
      (hex: string) => {
        const rgba = chroma(hex).rgba();
        setColor({
          rgba: {
            r: rgba[0],
            g: rgba[1],
            b: rgba[2],
            a: rgba[3],
          },
          hex,
        });
        debouncedChange?.({
          rgba: `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3]})`,
          hex,
          rgb: `rgb(${rgba[0]}, ${rgba[1]}, ${rgba[2]})`,
          opacity: rgba[3],
        });
      },
      [debouncedChange]
    );

    const handleCustomSelectColor = useCallback(
      (color: string) => {
        const asRgba = chroma(color).rgba();
        handleColorPickerChange({
          r: asRgba[0],
          g: asRgba[1],
          b: asRgba[2],
          a: asRgba[3],
        });
      },
      [handleColorPickerChange]
    );

    const eyeDropperAvailable = typeof window !== 'undefined' && !!window?.EyeDropper;

    return (
      <div
        className={`color-picker-container ${containerClassName || ''}`}
        style={
          {
            ...(saturationPickerHeight ? { '--saturationPickerHeight': saturationPickerHeight } : {}),
            ...(width ? { '--colorPickerWidth': width } : {}),
            ...(boxShadow ? { '--colorPickerBoxShadow': boxShadow } : {}),
          } as CSSProperties
        }
      >
        <div className="rgba-picker-container">
          <RgbaColorPicker
            color={color?.rgba}
            onChange={handleColorPickerChange}
            className={`color-picker ${pickerClassName || ''} ${eyeDropperAvailable ? 'eye-dropper-available' : ''}`}
            style={style || {}}
          />

          <OptionalComponent display={eyeDropperAvailable}>
            <div className="color-picker-eye-dropper-container">
              <EyeDropper onSelectColor={handleCustomSelectColor} />
            </div>
          </OptionalComponent>

          <div
            style={{ backgroundColor: `rgba(${color?.rgba.r}, ${color?.rgba.g}, ${color?.rgba.b}, ${color?.rgba.a})` }}
            className="square-32 radius-4 color-picker-display-block"
          />
        </div>

        <div className="flex fd-row jc-center ai-center color-picker-inputs-container">
          <div className="flex-column jc-center ai-center hex-color-input-wrapper flex-1">
            <HexColorInput
              id="hex-color-input"
              className="text-center radius-100 clear-input"
              color={color?.hex}
              onChange={handleHexInputChange}
            />
            <label className="width-100-percent text-center" htmlFor="hex-color-input">
              HEX
            </label>
          </div>

          <RGBAInputs
            red={red}
            green={green}
            blue={blue}
            alpha={alpha}
            setRed={setRed}
            setGreen={setGreen}
            setBlue={setBlue}
            setAlpha={setAlpha}
            debouncedChange={debouncedChange}
            setColor={setColor}
          />
        </div>

        <PresetColors colors={presetColors || []} onSelectColor={handleCustomSelectColor} label={presetColorsLabel} />
      </div>
    );
  }
);

export default ColorPicker;
